import  React, { Dispatch, SetStateAction, useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useAppSocketContext } from '@/shared/hooks';
import type { User, Lobby, LeaderboardEntry } from '@/shared/types';

// Jumping dots animation component
function JumpingDots() {
    return (
        <span className="jumping-dots">
            <span className="dot">.</span>
            <span className="dot">.</span>
            <span className="dot">.</span>
            <style>{`
                .jumping-dots {
                    display: inline-flex;
                    margin-left: 1px;
                }
                .jumping-dots .dot {
                    animation: jump 1.4s ease-in-out infinite;
                    font-weight: bold;
                }
                .jumping-dots .dot:nth-child(1) { animation-delay: 0s; }
                .jumping-dots .dot:nth-child(2) { animation-delay: 0.2s; }
                .jumping-dots .dot:nth-child(3) { animation-delay: 0.4s; }
                @keyframes jump {
                    0%, 60%, 100% { transform: translateY(0); }
                    30% { transform: translateY(-4px); }
                }
            `}</style>
        </span>
    );
}

function ViewableChessContent({
  user, 
  inGameSessionID, 
  gameID, 
  whiteID, 
  blackID, 
  whiteUsername, 
  blackUsername, 
  showChessGame, 
  currentRound, 
}
    :
    {
      user: User | null,
      inGameSessionID: number, 
      gameID: number, 
      whiteID: number, 
      blackID: number, 
      whiteUsername: string, 
      blackUsername: string, 
      showChessGame: boolean, 
      currentRound: number, 
}) {
    const { updatedLeaderboardRequest, setLeaderboardRequest, lobbyUpdateData, setLobbyUpdateData } = useAppSocketContext();
    const [showStandings, setShowStandings] = useState(true);
    const [lobby, setLobby] = useState<string[]>([]);
    const [releaseTime, setReleaseTime] = useState<string | null>(null);
    const [fetchedLeaderboard, setFetchedLeaderboard] = useState<LeaderboardEntry[] | null>(null);
    const [fetchError, setFetchError] = useState<string | null>(null);
    const { sessionID } = useParams<{ sessionID: string }>();

    const resetUpdatedLeaderboardRequest = () => setLeaderboardRequest(null);

    const fetchLobby = async () => {
        try {
            setFetchError(null);

            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/getLobby?tournamentId=${sessionID}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
            });

            if (!response.ok) {
            throw new Error(`Failed to fetch leaderboard: ${response.statusText}`);
            }

            const data: Lobby = await response.json();
            setLobby(data.usernames); 
            setReleaseTime(data.releaseTime);

        } catch (error: any) {
            setFetchError(error.message || "Unknown error fetching leaderboard");
        }
    }
    
    const fetchLeaderboard = useCallback(async () => {
      try {
        setFetchError(null);
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/getLeaderboard?tournamentId=${inGameSessionID}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch leaderboard: ${response.statusText}`);
        }

        const data: LeaderboardEntry[] = await response.json();
        setFetchedLeaderboard(data);
      } catch (error: any) {
        setFetchError(error.message || "Unknown error fetching leaderboard");
      }
    }, [inGameSessionID]);

    useEffect(() => {
      if (!fetchError || inGameSessionID) {
          fetchLobby();
          return
      }
  }, [fetchError, inGameSessionID]);

  // Handle real-time lobby updates from WebSocket
  useEffect(() => {
    if (lobbyUpdateData && lobbyUpdateData.tournamentId === Number(sessionID)) {
      setLobby(lobbyUpdateData.usernames);
      setReleaseTime(lobbyUpdateData.releaseTime);
      setLobbyUpdateData(null); // Clear after processing
    }
  }, [lobbyUpdateData, sessionID, setLobbyUpdateData]);

  const [timeLeft, setTimeLeft] = useState("");

  useEffect(() => {
    if (!releaseTime) return;

    const interval = setInterval(() => {
      const diff = new Date(releaseTime).getTime() - Date.now();

      if (diff <= 0) {
        setTimeLeft("STARTING_SOON");
        clearInterval(interval);
        return;
      }

      const hours = String(Math.floor(diff / (1000 * 60 * 60))).padStart(2, "0");
      const minutes = String(Math.floor((diff / (1000 * 60)) % 60)).padStart(2, "0");
      const seconds = String(Math.floor((diff / 1000) % 60)).padStart(2, "0");

      setTimeLeft(`${hours}:${minutes}:${seconds}`);
    }, 1000);

    return () => clearInterval(interval);
  }, [releaseTime]);

    useEffect(() => {
        if (!inGameSessionID) return;
        fetchLeaderboard();
      }, [inGameSessionID, fetchLeaderboard]);
      
      useEffect(() => {
        if (updatedLeaderboardRequest){
           if (updatedLeaderboardRequest.tournamentId !== inGameSessionID){
            resetUpdatedLeaderboardRequest();
            return;
          }
          // Use leaderboard data from WebSocket if available, otherwise fallback to HTTP fetch
          if (updatedLeaderboardRequest.leaderboard) {
            setFetchedLeaderboard(updatedLeaderboardRequest.leaderboard);
            resetUpdatedLeaderboardRequest();
          } else {
            // Fallback for backward compatibility
            fetchLeaderboard();
          }
        }
      }, [updatedLeaderboardRequest, inGameSessionID, fetchLeaderboard]);

    return (
        <div className="viewContainer">
            <div className="header" style={{ padding: 0, width: '100%'}}>
                {showChessGame 
                ? 
                <div style={{ display: 'flex', width: '100%' }}>
                    <div onClick={() => setShowStandings(prev => !prev)} className={`${!showStandings ? 'selectedColumn' : ''} standingHeaderButton`}>
                        <span>Game</span>
                    </div>
                    <div onClick={() => setShowStandings(prev => !prev)} className={`${showStandings ? 'selectedColumn' : ''} standingHeaderButton`}>
                        <span>Standings</span>
                    </div>
                </div>
                :
                <div className="standingHeaderButton">
                    <span>Standings</span>
                </div>
                }
            </div>
            {!inGameSessionID &&
              <div className="header lobbyTime" style={{ padding: 0, width: '100%'}}>
                  {timeLeft ? (
                    timeLeft === "STARTING_SOON" ? (
                      <span>Starting soon<JumpingDots /></span>
                    ) : (
                      timeLeft
                    )
                  ) : (
                    <div className="loader-dots">
                      <div></div>
                      <div></div>
                      <div></div>
                    </div>
                  )}
              </div>
            }
            <div className="scrollContainer">
                <div className="users">
                  <div>
                    {(() => {
                      return fetchedLeaderboard && fetchedLeaderboard.length > 0 && showStandings ? (
                        fetchedLeaderboard.map((entry, index) => (
                          <div
                            key={entry.userId}
                            className={`entry ${entry.userId === user?.id ? 'self' : ''}`}
                          >
                            <div className="place">#{index + 1}</div>
                            <div className="entryText">
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <h3 style={{ margin: 0 }} className="text-overflow">
                                  {entry.username || 'Lobby'}
                                </h3>
                                <div style={{ marginTop: '1px', height: '14px' }}>
                                  {/* Your SVG here */}
                                </div>
                              </div>
                              <div className="score">
                                <div className="textUppercase">
                                  {`${entry.points}/${(entry.round && entry.round > 0) ? entry.round : (currentRound || 1)}`}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : gameID && !showStandings ? (
                        <>
                          <div className={`entry ${whiteID === user?.id ? 'self' : ''}`}>
                            <div className="entryText">
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <h3 style={{ margin: 0 }} className="text-overflow">
                                  {whiteUsername || 'test'}
                                </h3>
                                <div style={{ marginTop: '1px', height: '14px' }}>
                                  {/* Your SVG here */}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className={`entry ${blackID === user?.id ? 'self' : ''}`}>
                            <div className="entryText">
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <h3 style={{ margin: 0 }} className="text-overflow">
                                  {blackUsername || ''}
                                </h3>
                                <div style={{ marginTop: '1px', height: '14px' }}>
                                  {/* Your SVG here */}
                                </div>
                              </div>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="lobbyEmpty">
                          {lobby?.map((username, index) => (
                            <div className="entry" key={index}>
                              <div className="place">#{index + 1}</div>
                              <div className="entryText">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <h3 style={{ margin: 0 }} className="text-overflow">
                                    {username || 'Lobby'}
                                  </h3>
                                  <div style={{ marginTop: '1px', height: '14px' }}>
                                    {/* Your SVG here */}
                                  </div>
                                </div>
                                <div className="score">
                                  <div className="textUppercase">
                                    {`0/0`}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>                                       
                </div>
            </div>
        </div>
    )
}

export default ViewableChessContent;
